// Staff auth-token storage.  "Remember me" controls whether the session
// survives an app close.
//
// **Browser**:
//   • remember = true  → token in localStorage (survives close)
//   • remember = false → token in sessionStorage (clears on close, secure default)
//
// **Electron** (different mechanism, same outcome):
//   • Token ALWAYS lives in localStorage.  Reason: Electron's
//     sessionStorage is per-renderer-process, so a freshly-spawned
//     BrowserWindow (popped-out timer, matter quick-view) can't see the
//     parent window's sessionStorage.  We need localStorage for multi-
//     window auth sharing via the 'persist:lextrack' partition.
//   • A separate `lextrack_remember` flag in localStorage records the
//     user's choice.  When the flag is missing, Electron's main process
//     clears the token on `before-quit` (see desktop/main.js) so the
//     "clears on close" promise still holds — the user has to sign in
//     again next launch, exactly as the browser default behaves.
//   • Net result for the user: tick the box → stay logged in across
//     restarts.  Don't tick → sign in every relaunch.  Same UX as a
//     well-behaved browser app.
const KEY = 'lextrack_token'
const REMEMBER_KEY = 'lextrack_remember'
const isElectron = () => typeof window !== 'undefined' && !!window.electronAPI

export const getToken = () =>
  localStorage.getItem(KEY) || sessionStorage.getItem(KEY)

export const setToken = (token, remember) => {
  if (isElectron()) {
    // Always localStorage; remember just toggles the persistence flag the
    // main process checks on quit.
    localStorage.setItem(KEY, token)
    sessionStorage.removeItem(KEY)
    if (remember) localStorage.setItem(REMEMBER_KEY, '1')
    else localStorage.removeItem(REMEMBER_KEY)
    return
  }
  if (remember) {
    localStorage.setItem(KEY, token)
    sessionStorage.removeItem(KEY)
  } else {
    sessionStorage.setItem(KEY, token)
    localStorage.removeItem(KEY)
  }
}

export const clearToken = () => {
  localStorage.removeItem(KEY)
  sessionStorage.removeItem(KEY)
  localStorage.removeItem(REMEMBER_KEY)
}

// One-time migration on app start: if an existing user's token is in
// sessionStorage (pre-Electron-fix), move it to localStorage so their
// pop-out windows inherit auth without forcing a re-login.  Does NOT
// set the remember flag — that's an explicit user choice on next login.
export const migrateSessionTokenIfElectron = () => {
  if (!isElectron()) return
  const sessTok = sessionStorage.getItem(KEY)
  if (sessTok && !localStorage.getItem(KEY)) {
    localStorage.setItem(KEY, sessTok)
    sessionStorage.removeItem(KEY)
  }
}

// Was the last session a "remember me" one? Used to pre-check the login checkbox.
export const wasRemembered = () => isElectron()
  ? !!localStorage.getItem(REMEMBER_KEY)
  : !!localStorage.getItem(KEY)

// Single entry point for spawning Electron pop-out windows.
//
// Runs sessionStorage→localStorage migration first AND threads the current
// auth token through the popup URL via a hash fragment (`#auth=<token>`).
// The popup's AuthContext consumes the hash on mount (see consumeAuthHash
// below), persists into its own localStorage, then scrubs it from the URL.
//
// Why the hash bridge: today EVERY BrowserWindow — main window, pop-outs and
// the floating timer bar — uses the `persist:lextrack` partition
// (desktop/main.js), so they all share one localStorage: the token, and the
// timer slots the Timers panel, the bar and every timer form read and write.
// The bridge dates from builds where the main window used Electron's default
// session and could not share storage with a pop-out; it is kept as a
// harmless fallback (the pop-out writes the same token it would have read).
// Hash fragments are NOT sent in HTTP requests and never hit Apache / proxy
// logs, so they're a reasonable in-process token courier.  We scrub the hash
// immediately on read so the token doesn't linger.
//
// Returns whatever electronAPI.openPopupWindow returns (a Promise), or
// null if not in Electron.
export const safeOpenPopupWindow = (popupKey, route, options = {}) => {
  migrateSessionTokenIfElectron()
  if (!isElectron() || !window.electronAPI?.openPopupWindow) return null
  // Append auth=<token> as a URL hash fragment so the popup can adopt
  // the current session without needing partition-shared storage.
  const tok = getToken()
  let finalRoute = route
  if (tok) {
    const sep = route.includes('#') ? '&' : '#'
    finalRoute = `${route}${sep}auth=${encodeURIComponent(tok)}`
  }
  try {
    return window.electronAPI.openPopupWindow(popupKey, finalRoute, options)
  } catch {
    return null
  }
}

// Read & consume the `#auth=<token>` hash fragment if present.  Called
// once on app mount.  If a token is found in the hash:
//   1. write it to localStorage so subsequent reads via getToken() succeed
//   2. scrub the hash from the URL so the token isn't visible after load
//      or carried into bookmarks / window titles
// Returns true if a hash token was consumed (so AuthContext can skip the
// usual no-token redirect).
export const consumeAuthHashIfPresent = () => {
  if (typeof window === 'undefined') return false
  const hash = window.location.hash || ''
  if (!hash || !hash.includes('auth=')) return false
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  const tok = params.get('auth')
  if (!tok) return false
  // Persist in localStorage so the rest of the app + a refresh both work.
  localStorage.setItem(KEY, tok)
  // Strip the hash so the token doesn't linger in the URL bar.
  // Keep any other hash params that weren't 'auth='.
  params.delete('auth')
  const remaining = params.toString()
  const newUrl = window.location.pathname + window.location.search + (remaining ? `#${remaining}` : '')
  window.history.replaceState({}, '', newUrl)
  return true
}
